import dateFormat from "dateformat";
import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { DataLayerArgs } from "react-gtm-module";
import { Cart, Stay } from "rsl-api-106/lib/types";

import { useSession } from "@hooks";
import {
  sendDataLayer,
  sendDataLayerAddPaymentInfo,
  sendDataLayerBeginCheckout,
  sendDataLayerCheckoutActivities,
  sendDataLayerViewCart,
} from "@utils/tracking/gtm";

type GTMReservationPageProps = {
  cart: Cart;
};

const getRoomReservationCode = (cart: Cart): string => {
  let code: string[] = [];
  cart?.reservations?.forEach((resa) => {
    resa?.stays?.forEach((stay) => {
      stay?.distribution?.roomTypes?.forEach((ri) => {
        code = [...code, ri?.code!!];
      });
    });
  });
  return code.join(" / ");
};

const getBaseProduct = (cart: Cart): string => {
  let code: string[] = [];
  cart?.reservations?.forEach((resa) => {
    resa?.stays?.forEach((stay) => {
      stay?.occupants?.forEach((occupant) => {
        occupant?.lineItems?.forEach((lineitem) => {
          if (lineitem?.product?.isBase) {
            code = [...code, lineitem?.product?.label!!];
          }
        });
      });
    });
  });
  return code.join(" , ");
};

const getDataLayer = (
  lang: string,
  cart: Cart,
  step: number,
  event?: string
): object => {
  return {
    typepage: "panier",
    pagename: "panier",
    userid: "31494",
    connexion: "0",
    langue: lang.toLowerCase(),
    site: "vacancéole",
    montant: cart.reservations!![0]?.totalAmount?.amount?.toFixed(2),
    code_promo: cart.reservations!![0]?.discountCode,
    event: event ? event : "checkout",
    ecommerce: {
      checkout: {
        actionField: { step: `${step}` },
        products: [
          {
            name: getRoomReservationCode(cart),
            id: cart.reservations!![0]?.property?.id,
            price: cart.reservations!![0]?.totalAmount?.amount?.toFixed(2),
            brand: cart.reservations!![0]?.property?.label,
            category: "",
            quantity: 1,
            formule: getBaseProduct(cart),
            participant: cart.reservations!![0]?.stays!![0]?.occupants?.length,
            duree_sejour: cart.reservations!![0]?.nbDays,
            date_arrivee: cart.reservations!![0]?.startDate,
            date_depart: cart.reservations!![0]?.endDate,
          },
        ],
      },
    },
    criteo_items: [
      {
        id: cart.reservations!![0]?.property?.id,
        price: cart.reservations!![0]?.totalAmount?.amount?.toFixed(2),
        quantity: "1",
      },
    ],
  };
};

const sendDataLayerStepPageView = (lang: string, typepage: string) => {
  sendDataLayer({
    event: "view_page",
    page: {
      page_path: window.location.href, //https://www.vacanceole.com/		"page_title": "$_META_TITLE",//Vacancéole : Location Vacances XXXXX
      pagename: window.location.pathname, //= pagename
      typepage: typepage, // = reprise du nom de l’étape (nom technique par exemple)
      langue: lang, // = langue
      site: "vacancecole", // = site
    },
  });
};

export const GTMReservationPageStep1: React.FC<GTMReservationPageProps> = ({
  cart,
}) => {
  /* Hooks */
  const { lang } = useSession();

  useEffect(() => {
    //sendDataLayer(getDataLayer(lang, cart, 1));
    //sendDataLayerStepPageView(lang, "Authentification");
    sendDataLayerViewCart(cart);
  }, []);

  return <></>;
};

export const GTMReservationPageStep2: React.FC<GTMReservationPageProps> = ({
  cart,
}) => {
  /* Hooks */
  const { lang, funnel_type } = useSession();

  useEffect(() => {
    /*const items = handleGetGTMItems(cart);
    sendDataLayer({
      event: "add_to_cart",
      currency: cart?.reservations!![0]?.totalAmount?.currencyCode,
      participant: handleGetTotalOccupant(cart),
      duree_sejour: handleGetTotalDuration(cart),
      date_arrivee: dateFormat(
        new Date(cart?.reservations!![0]?.startDate),
        "ddmmyyyy"
      ),
      date_depart: dateFormat(
        new Date(cart?.reservations!![0]?.endDate),
        "ddmmyyyy"
      ),
      ecommerce: {
        items: items,
      },
    });
    sendDataLayerStepPageView(lang, "Prestation");*/
    sendDataLayerBeginCheckout(cart, funnel_type);
  }, []);

  return <></>;
};

export const GTMReservationPageStep3: React.FC<GTMReservationPageProps> = ({
  cart,
}) => {
  /* Hooks */
  const { lang, funnel_type } = useSession();

  useEffect(() => {
    //sendDataLayer(getDataLayer(lang, cart, 3));
    //sendDataLayerStepPageView(lang, "Occupants");
    sendDataLayerCheckoutActivities(cart, funnel_type);
  }, []);

  return <></>;
};

export const GTMReservationPageStep4: React.FC<GTMReservationPageProps> = ({
  cart,
}) => {
  /* Hooks */
  const { lang, funnel_type } = useSession();

  useEffect(() => {
    /*const items = handleGetGTMItems(cart);
    const totalOccupant = handleGetTotalOccupant(cart);
    const duration = handleGetTotalDuration(cart);
    sendDataLayer({
      event: "checkout_assurance",
      currency: cart?.reservations!![0]?.totalAmount?.currencyCode,
      participant: totalOccupant,
      duree_sejour: duration,
      date_arrivee: dateFormat(
        new Date(cart?.reservations!![0]?.startDate),
        "ddmmyyyy"
      ),
      date_depart: dateFormat(
        new Date(cart?.reservations!![0]?.endDate),
        "ddmmyyyy"
      ),
      ecommerce: {
        items: items,
      },
    });

    sendDataLayerStepPageView(lang, "Paiement");*/

    sendDataLayerAddPaymentInfo(cart, funnel_type);
  }, []);

  return <></>;
};

const handleGetGTMItems = (cart: Cart) => {
  let items: any = [];
  cart?.reservations?.forEach((r) => {
    r?.stays?.forEach((s) => {
      items = [
        ...items,
        {
          currency: s?.totalAmount?.currencyCode,
          item_id: r.property?.id,
          item_name: r.property?.label,
          item_category: s?.distribution?.roomTypes
            ?.map((x) => x?.code)
            .join(","),
          item_category2: "VACANCEOLE location de particulier", //ou location de particulier ou partenaire
          item_brand: "Vacanceole",
          item_variant: getFormuleSejour(s!!), //Formule location – Studio 2 personnes - Twin
          price: s?.totalAmount?.amount?.toFixed(2),
          quantity: handleGetNbRoom(s!!), //ATTENTION à DYNAMISER EN FONCTION DU CHOIX
        },
      ];
    });
  });

  return items;
};

const handleGetNbRoom = (stay: Stay) => {
  let n = 0;

  stay?.distribution?.roomTypes?.forEach((ri) => {
    n += ri?.quantity ?? 0;
  });

  return n;
};

const getFormuleSejour = (stay: Stay) => {
  let s = "";
  stay.occupants?.forEach((o) => {
    o?.lineItems?.forEach((l) => {
      if (l?.product?.isBase) {
        s = l.product.label!!;
      }
    });
  });

  return s;
};

const handleGetTotalOccupant = (cart: Cart) => {
  let n = 0;
  cart?.reservations?.forEach((r) => {
    r?.stays?.forEach((s) => {
      n += s?.occupants?.length ?? 0;
    });
  });

  return n;
};

const handleGetTotalDuration = (cart: Cart) => {
  let n = 0;
  cart?.reservations?.forEach((r) => {
    r?.stays?.forEach((s) => {
      n += s?.nbDays ?? 0;
    });
  });

  return n;
};
